import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { arrayMove } from '@dnd-kit/sortable'
import { PlusIcon } from '@phosphor-icons/react'

import { Button, Divider, Text, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import usePackagingList from './hooks/usePackagingList/usePackagingList'
import SortablePackagingList from './SortablePackagingList/SortablePackagingList'
import * as Styles from './styles'

const PackagingConfiguration = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()
  const isEditable = hasPermission(userPermissions.editLogistics)

  const {
    packaging,
    setPackaging,
    fetchingPackaging,
    handleChangeEnabled,
    onSave,
    editingPackaging,
    onDelete,
    onEdit,
    onDuplicate,
    onCreate,
    onSetDefault,
    revertChanges,
    hasChanges,
    onSearch,
    loadMore,
    hasNextPage,
    totalCount,
  } = usePackagingList({
    storeId: info?.selectedStore?.id,
    setToastInfo,
  })

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: packaging,
    paginated: true,
    hasNextPage,
    onLoadMore: loadMore,
    totalCount,
  })

  const [dragging, setDragging] = useState(false)

  const onSortEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = packaging.findIndex(r => r.id === active.id)
    const newIndex = packaging.findIndex(r => r.id === over.id)

    setPackaging(packaging => [...arrayMove(packaging, oldIndex, newIndex)])
    setDragging(false)
    document.body.style.cursor = 'inherit'
  }

  const onSortStart = () => {
    setDragging(true)
    document.body.style.cursor = 'grabbing'
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      <Text type="bodyMRegular">
        {t('Define los tipos de embalaje y sus dimensiones que se usan para preparar envíos')}:
      </Text>
      <QueryFilter placeholder={t('Buscar por nombre')} onChange={onSearch} />
      <SortablePackagingList
        packagingItems={elementsToShow}
        onChangeEnabled={handleChangeEnabled}
        onDelete={onDelete}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onSetDefault={onSetDefault}
        dragging={dragging}
        onSortEnd={onSortEnd}
        onSortStart={onSortStart}
        loading={fetchingPackaging}
        isEditable={isEditable}
      />
      <Divider />
      <LoadMore
        numberOfElementsToShow={numberOfElementsToShow}
        totalElements={totalElements}
        hasMoreElements={hasMoreElements}
        showMore={showMore}
      />
      {isEditable && (
        <Styles.ActionsContainer>
          <Button variant="text" onClick={onCreate}>
            <PlusIcon size={24} />
            {t('Añadir nuevo tipo de embalaje')}
          </Button>
        </Styles.ActionsContainer>
      )}
      <SaveChangesButtons
        hasChanges={isEditable && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={editingPackaging}
        disabled={editingPackaging}
      />
    </Styles.Container>
  )
}

export default PackagingConfiguration
